import React, { useState, useRef, useEffect } from "react";
import { TextField, Button, IconButton } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ContextNoteDialog from "../components/dialogs/ContextNoteDialog";
import { useStateContext } from "../contexts/ContextProvider";
import StackChart from "../components/charts/StackChart";
import TableData from "../components/versatile/TableData";
import axios from "axios";
import CircularProgress from '@mui/material/CircularProgress';

// ── shared label style (matches SceneBuilder footer / header labels) ──────────
const metaLabel = {
  fontSize: 13, fontFamily: "'Barlow', sans-serif",
  fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase",
  color: "rgba(255,255,255,0.45)",
};

const metaValue = {
  color: "rgba(255,255,255,0.72)", fontFamily: "'Barlow', sans-serif",
  fontWeight: 300, fontSize: 15,
};

// ── divider ───────────────────────────────────────────────────────────────────
const VDivider = () => (
  <div style={{ width: 1, height: 14, background: "rgba(255,255,255,0.07)", flexShrink: 0 }} />
);

// ── dialog data ───────────────────────────────────────────────────────────────
const holderDialogData = {
  title:      "Holder Structure — Vision Tools",
  subtitle:   "Solana token analytics · Real-time data",
  lamp:       "#10b981",
  brand:      "Vision Tools · Holder Structure",
  storageKey: "holderStructureNoteEnabled",
  tags: [
    { label: "Solana",          color: "purple" },
    { label: "Token Analytics", color: "blue"   },
    { label: "Real-time",       color: "green"  },
  ],
  sections: [
    {
      label: "What is this?",
      body: `A distribution map of <strong style="color:rgba(255,255,255,0.88)">when and where holders bought in</strong>.
             Each bar represents a wallet balance range — the height shows total tokens accumulated in that range.
             The color shows <strong style="color:rgba(255,255,255,0.88)">how old each position is</strong>,
             from recent buys (purple) to older holds (green/yellow).`,
    },
    {
      label: "How to read it?",
      body: `<strong style="color:rgba(255,255,255,0.88)">X axis — balance ranges</strong> (0–50k, 50–100k, etc.)
             show where holders are sized. Wider bars mean more wallets clustered at that balance tier.
             <br/><br/>
             <strong style="color:rgba(255,255,255,0.88)">Bar height</strong> is the total accumulated tokens
             in that range — taller means more supply concentrated there.
             <br/><br/>
             <strong style="color:rgba(255,255,255,0.88)">Color = position age.</strong>
             Purple is newest (5m–1h), blue is mid (2h–24h), green/teal is older (1d–1w).
             Each buy is tracked independently — if a wallet bought twice,
             both buys appear as separate colored segments in their own range.`,
    },
    {
      label: "What to look for?",
      body: `Heavy purple at low ranges → large positions built over time.
             <br/>
             Tall blue/teal bars at high ranges → potential smart money.fresh retail buying at small sizes.
             <br/>
             A single range dominating in one color → coordinated entry at a specific price point.`,
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────

const HoldersStructure = () => {
  const aboutDialogRef = useRef();
  const { activeMenu, holderChart, isLogged } = useStateContext();
  const [isLoading, setIsLoading] = useState(false);

  const [tokenCA, setTokenCA]         = useState("");
  const [reqData, setReqData]         = useState(null);
  const [chartData, setChartData]     = useState(null);
  const [tableData, setTableData]     = useState(null);
  const [tokenSupply, setTokenSupply] = useState(null);
  const [error, setError]             = useState(null);

  const defaultTokenData = {
    symbol:          "—",
    contractAddress: "xxxx...xxxx",
    time:            "—",
    totalSupply:     "—",
    netTransfers:    "—",
  };

  const formatToMillions      = (v) => v ? (v / 1_000_000).toFixed(1) + "M" : "0M";
  const formatContractAddress = (ca) => ca ? `${ca.slice(0, 4)}...${ca.slice(-4)}` : "xxxx...xxxx";

  const fetchData = async () => {
    if (!tokenCA) { alert("Please enter a valid Contract Address (CA)"); return; }
    setError(null);
    setIsLoading(true);
    try {
      const response = await axios.post("https://vibecat.me/api/hsinfo", { tokenCA });
      setReqData(response.data);
    } catch (err) {
      setError("Failed to fetch data. Please try again.");
      console.error("❌ API Error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (reqData) {
      setChartData([...reqData.dataChart]);
      setTableData([...reqData.holders]);
      setTokenSupply(reqData.token.totalSupply);
    }
  }, [reqData]);

  useEffect(() => {
    const disabled = localStorage.getItem("holderStructureNoteEnabled") === "false";
    if (!disabled) {
      setTimeout(() => aboutDialogRef.current?.handleOpen(), 600);
    }
  }, []);

  const tokenData = reqData
    ? {
        symbol:          reqData.token.symbol,
        contractAddress: formatContractAddress(tokenCA),
        time:            new Date(reqData.token.dateTimeNow).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        totalSupply:     formatToMillions(reqData.token.totalSupply),
        netTransfers:    formatToMillions(reqData.token.totalOutflowInflow),
      }
    : defaultTokenData;

  return (
    <div style={{ padding: "20px 24px", fontFamily: "'Barlow', sans-serif" }}>

      {/* ── Input row ── */}
      <div style={{
        display: "flex", alignItems: "center",
        gap: 12, maxWidth: 680, margin: "0 auto 20px",
      }}>
        <span style={{
          ...metaLabel, whiteSpace: "nowrap", fontSize: 12,
          color: "rgba(255,255,255,0.5)",
        }}>
          Sol Token CA
        </span>

        <TextField
          variant="outlined"
          placeholder="Paste contract address"
          autoComplete="off"
          value={tokenCA}
          onChange={(e) => setTokenCA(e.target.value)}
          sx={{
            flexGrow: 1,
            input: {
              color: "rgba(255,255,255,0.78)",
              fontFamily: "'Barlow', sans-serif",
              fontSize: 13,
              padding: "9px 14px",
            },
            "& .MuiOutlinedInput-root": {
              borderRadius: 0,
              "& fieldset":             { borderColor: "rgba(255,255,255,0.12)" },
              "&:hover fieldset":       { borderColor: "rgba(255,255,255,0.28)" },
              "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.4)"  },
            },
          }}
        />

        <Button
          variant="outlined"
          onClick={isLogged ? fetchData : () => window.location.href = "/Login"}
          disabled={isLoading}
          sx={{
            fontFamily: "'Barlow', sans-serif", fontSize: 13, fontWeight: 600,
            letterSpacing: "0.06em", textTransform: "none",
            px: 2.5, py: 0.9, borderRadius: 0,
            borderColor: "rgba(156,163,175,0.5)",
            color: "rgba(255,255,255,0.78)",
            transition: "all 0.3s ease",
            position: "relative",
            "&:hover": {
              borderColor: "transparent",
              borderImage: "linear-gradient(to right,#8018e7,#10b981,#3b82f6) 1",
              color: "#fff",
            },
            "&.Mui-disabled": { opacity: 0.4 },
          }}
        >
          {isLogged ? "Submit" : "Login to use"}
          {isLoading && (
            <CircularProgress
              size={14}
              sx={{
                color: "rgba(255,255,255,0.8)",
                position: "absolute", right: -22,
                top: "50%", transform: "translateY(-50%)",
              }}
            />
          )}
        </Button>
      </div>

      {/* ── Error ── */}
      {error && (
        <div style={{
          maxWidth: 680, margin: "0 auto 12px",
          fontSize: 12, fontFamily: "monospace",
          color: "#ef4444", letterSpacing: "0.03em",
          padding: "6px 12px",
          background: "rgba(239,68,68,0.08)",
          border: "1px solid rgba(239,68,68,0.25)",
        }}>
          {error}
        </div>
      )}

      {/* ── Token meta bar ── */}
      <div style={{
        display: "flex", alignItems: "center",
        gap: 16, flexWrap: "wrap",
        maxWidth: "90%", margin: "0 auto 20px",
        padding: "10px 16px",
        borderTop: "1px solid rgba(255,255,255,0.06)",
        borderBottom: "1px solid rgba(255,255,255,0.06)",
      }}>

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={metaLabel}>Supply</span>
          <span style={metaValue}>{tokenData.totalSupply}</span>
        </div>

        <VDivider />

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={metaLabel}>Net In</span>
          <span style={metaValue}>{tokenData.netTransfers}</span>
        </div>

        <VDivider />

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={metaLabel}>Name</span>
          <span style={metaValue}>{tokenData.symbol}</span>
        </div>

        <VDivider />

        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={metaLabel}>Time</span>
          <span style={metaValue}>{tokenData.time}</span>
        </div>

        <VDivider />

        {/* CA + copy */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={metaLabel}>CA</span>
          <span style={metaValue}>{tokenData.contractAddress}</span>
          <IconButton
            size="small"
            onClick={() => navigator.clipboard.writeText(tokenCA)}
            title="Copy to Clipboard"
            sx={{
              color: "rgba(255,255,255,0.28)", padding: "2px",
              "&:hover": { color: "#10b981" },
            }}
          >
            <ContentCopyIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </div>

        {/* flex spacer */}
        <div style={{ flex: 1 }} />

        {/* About - Help */}
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); aboutDialogRef.current.handleOpen(); }}
            style={{
              display: "flex", alignItems: "center", gap: 4,
              fontSize: 14, fontFamily: "'Barlow', sans-serif",
              fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase",
              color: "rgba(255,255,255,0.48)", textDecoration: "none",
              transition: "color 0.2s",
            }}
            onMouseEnter={e => e.currentTarget.style.color = "rgba(255,255,255,0.75)"}
            onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.48)"}
          >
            About · Help
            <HelpOutlineIcon sx={{ fontSize: 15 }} />
          </a>
        </div>

      </div>

      {/* ── Chart ── */}
      <div style={{ width: "90%", margin: "0 auto", paddingLeft: "5%" }}>
        <StackChart input={chartData} tokenSupply={tokenSupply} />
      </div>

      {/* ── Table ── */}
      <div style={{ width: "89%", margin: "16px auto 0" }}>
        <TableData input={tableData} tokenSupply={tokenSupply} />
      </div>

      {/* ── Footer ── */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        marginTop: 48, marginBottom: 24,
        borderTop: "1px solid rgba(255,255,255,0.06)",
        paddingTop: 16,
      }}>
        <span style={{
          fontSize: 13, fontWeight: 500, fontFamily: "'Barlow', sans-serif",
          letterSpacing: "0.1em", textTransform: "uppercase",
          background: "linear-gradient(to right, #10b981, #3b82f6)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
        }}>
          © {new Date().getFullYear()} Vision Tools · All rights reserved
        </span>
      </div>

      {/* ── Dialog ── */}
      <ContextNoteDialog ref={aboutDialogRef} data={holderDialogData} />
    </div>
  );
};

export default HoldersStructure;